import React, { useMemo } from "react";

import { CalendarDay } from "~/shared/types";
import { getFormattedTimezone } from "~/shared/utils/timezone";
import { tw } from "~/utils";
import { ChevronLeftIcon } from "../icons/ChevronLeftIcon";
import { ChevronRightIcon } from "../icons/ChevronRightIcon";

interface CalendarSelectorProps {
  month: string;
  day: number;
  dayName: string;
  daysInView: CalendarDay[];
  onDateSelect: (date: Date) => void;
  onNavigatePrev: (() => void) | null;
  onNavigateNext: () => void;
  selectedDate: Date | null;
  loading?: boolean;
  isMobile?: boolean;
}

enum OrdinalSuffix {
  ST = "st", // 1st, 21st, 31st
  ND = "nd", // 2nd, 22nd
  RD = "rd", // 3rd, 23rd
  TH = "th", // 4th, 5th, etc.
}

const getOrdinalSuffix = (day: number): string => {
  const TEENS_START = 4;
  const TEENS_END = 20;
  const FIRST = 1;
  const SECOND = 2;
  const THIRD = 3;

  if (day >= TEENS_START && day <= TEENS_END) {
    return OrdinalSuffix.TH;
  }

  const lastDigit = day % 10;

  switch (lastDigit) {
    case FIRST:
      return OrdinalSuffix.ST;
    case SECOND:
      return OrdinalSuffix.ND;
    case THIRD:
      return OrdinalSuffix.RD;
    default:
      return OrdinalSuffix.TH;
  }
};

// "Wednesday, August 12th" — full weekday and month in every view; the month
// is never abbreviated, including on mobile.
const formatDateWithOrdinal = (date: Date): React.ReactNode => {
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });

  return (
    <>
      {weekday}, {month} {day}
      <sup className="pl-0.5 text-xs">{getOrdinalSuffix(day)}</sup>
    </>
  );
};

// Skeleton loader for a single day
const DaySkeleton: React.FC = () => (
  <div className="max-w-[60px] flex-1 animate-pulse rounded-xl border border-neutral-300 bg-gray-50 p-2 text-center">
    <div className="mx-auto mb-1 h-3 w-8 rounded bg-gray-200" />
    <div className="mx-auto h-6 w-6 rounded bg-gray-200" />
  </div>
);

// Single day button component
const DayButton: React.FC<{
  day: CalendarDay;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ day, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    disabled={!day.available}
    className={tw(
      "max-w-[60px] flex-1 rounded-xl border-2 p-2 text-center transition-all duration-200",
      isSelected && "border-green-600 bg-green-600 text-white shadow-md",
      day.available &&
        !isSelected &&
        "cursor-pointer border-green-500 bg-green-50 text-neutral-700 hover:bg-green-100 hover:shadow-sm",
      !day.available &&
        "cursor-not-allowed border-gray-200 bg-gray-50 text-neutral-400 opacity-50",
    )}
  >
    <div className="mb-1 text-xs">
      {day.date.toLocaleDateString("en-US", { weekday: "short" })}
    </div>
    <div className="font-display text-lg sm:text-xl">{day.date.getDate()}</div>
    {/* Availability indicator dot */}
    {day.available && !isSelected && (
      <div className="mx-auto mt-1 size-1.5 rounded-full bg-green-500" />
    )}
  </button>
);

const CalendarSelector: React.FC<CalendarSelectorProps> = ({
  month,
  day,
  dayName,
  daysInView,
  onDateSelect,
  onNavigatePrev,
  onNavigateNext,
  selectedDate,
  loading = false,
  isMobile = false,
}) => {
  const visibleDays = useMemo(
    () => (isMobile ? daysInView.slice(0, 3) : daysInView),
    [isMobile, daysInView],
  );

  const skeletonCount = isMobile ? 3 : 7;

  // Determine header date to display
  const headerContent = useMemo(() => {
    if (selectedDate) {
      return formatDateWithOrdinal(selectedDate);
    }

    // Show the first day in view when no selection
    if (visibleDays.length > 0) {
      return formatDateWithOrdinal(visibleDays[0].date);
    }

    // Fallback to current day
    return (
      <>
        {dayName}, {month} {day}
        <sup className="pl-0.5 text-xs">{getOrdinalSuffix(day)}</sup>
      </>
    );
  }, [selectedDate, visibleDays, dayName, day, month]);

  return (
    <div className="rounded-lg border border-gray-300 bg-white p-3 sm:p-4">
      <div className="mb-3 flex flex-col gap-1 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-medium sm:text-base">
          {loading ? (
            <div className="h-5 w-36 animate-pulse rounded bg-gray-200 sm:h-6 sm:w-48" />
          ) : (
            headerContent
          )}
        </h3>
        <div className="text-[10px] text-neutral-500 sm:mt-0 sm:text-[10px]">
          Times in {getFormattedTimezone()}
        </div>
      </div>

      <div className="flex items-center justify-center gap-1 sm:gap-2">
        <button
          onClick={onNavigatePrev ?? (() => {})}
          className={tw(
            "rounded-full p-2 transition-colors sm:p-2",
            onNavigatePrev && !loading
              ? "cursor-pointer hover:bg-gray-100 active:bg-gray-200"
              : "cursor-not-allowed opacity-40",
          )}
          aria-label="Previous week"
          disabled={!onNavigatePrev || loading}
        >
          <ChevronLeftIcon className="size-5" />
        </button>

        <div className="flex flex-1 justify-center gap-2 sm:gap-3">
          {loading ? (
            // Show skeletons during loading
            Array.from({ length: skeletonCount }).map((_, index) => (
              <DaySkeleton key={`skeleton-${index}`} />
            ))
          ) : (
            // Show actual days with fade-in animation
            <div className="animate-fadeIn flex flex-1 justify-center gap-2 sm:gap-3">
              {visibleDays.map((calendarDay) => {
                const isSelected =
                  selectedDate &&
                  selectedDate.getDate() === calendarDay.date.getDate() &&
                  selectedDate.getMonth() === calendarDay.date.getMonth() &&
                  selectedDate.getFullYear() === calendarDay.date.getFullYear();

                return (
                  <DayButton
                    key={calendarDay.date.toISOString()}
                    day={calendarDay}
                    isSelected={!!isSelected}
                    onSelect={() => onDateSelect(calendarDay.date)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={onNavigateNext}
          className={tw(
            "rounded-full p-2 transition-colors sm:p-2",
            !loading
              ? "cursor-pointer hover:bg-gray-100 active:bg-gray-200"
              : "cursor-not-allowed opacity-40",
          )}
          aria-label="Next week"
          disabled={loading}
        >
          <ChevronRightIcon className="size-5" />
        </button>
      </div>

      {/* Searching indicator during auto-navigation */}
      {loading && (
        <div className="mt-3 text-center text-xs text-neutral-500">
          Searching for available appointments...
        </div>
      )}
    </div>
  );
};

export default CalendarSelector;
